import { useState } from 'react';

import { getFundingRequirement } from '@helpers/FundingRequirements/getFundingRequirement';
import { getIsFundingRequirementActionable } from '@helpers/FundingRequirements/getIsFundingRequirementActionable';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { IconDownload } from '@iwoca/icons';
import {
    Button,
    CheckBox,
    ErrorMessage,
    Flex,
    Stack,
    Text
} from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation } from 'react-router-dom';

import { CreditAgreementNotice } from './CreditAgreementNotice';
import { useCreditAgreement } from './useCreditAgreement';
import { useGetFundingRequirements } from '../../../../hooks/useGetFundingRequirements';
import { FUNDING_REQUIREMENTS_LIST_PATH } from '../../../../requirementStepPaths';
import { DocumentViewer } from '../../../DocumentViewer/DocumentViewer';

export const CreditAgreementPage = () => {
    const { search } = useLocation();
    const { requirements } = useGetFundingRequirements();
    const requirement = getFundingRequirement(requirements, 'credit_agreement');

    if (!requirement || !getIsFundingRequirementActionable(requirement)) {
        return (
            <Redirect
                to={maintainStateKey({
                    pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                    search
                })}
            />
        );
    }

    if (!requirement.document_id) {
        const message = 'CreditAgreementPage: requirement has no document_id';
        logError(message, { tags: TAGS.DE_LENDING });
        throw new Error(message);
    }

    return <SignCreditAgreement documentId={requirement.document_id} />;
};

const SignCreditAgreement = ({ documentId }: { documentId: string }) => {
    const [hasReadToEnd, setHasReadToEnd] = useState(false);
    const [hasAgreed, setHasAgreed] = useState(false);
    const [hasTriedToSign, setHasTriedToSign] = useState(false);
    const { downloadUrl, sign, isSigning, isSignError } =
        useCreditAgreement(documentId);

    const handleSign = () => {
        setHasTriedToSign(true);
        if (hasReadToEnd && hasAgreed) {
            sign();
        }
    };

    return (
        <Stack gap="section">
            <Stack>
                <Text className="text-3xl font-bold">
                    <FormattedMessage
                        id="_.funding_requirements_v2.credit_agreement.title"
                        defaultMessage="Your credit agreement"
                    />
                </Text>
                <Text className="text-xl">
                    <FormattedMessage
                        id="_.funding_requirements_v2.credit_agreement.subtitle"
                        defaultMessage='Please read the agreement and click "Sign" below to continue.'
                    />
                </Text>
            </Stack>

            <Stack>
                <DocumentViewer
                    documentId={documentId}
                    onReadToEnd={() => setHasReadToEnd(true)}
                />
                <CreditAgreementNotice
                    hasReadToEnd={hasReadToEnd}
                    showReadToEndError={hasTriedToSign}
                    isSignError={isSignError}
                />
            </Stack>

            <Stack>
                <Stack gap="tight">
                    {/* ORION-113-GAP component: the root of a compound control (CheckBox.Root) can't take layout props, so its row layout stays a class */}
                    <CheckBox.Root className="flex items-start">
                        <CheckBox.Control
                            className="shrink-0"
                            checked={hasAgreed}
                            onChange={(event) =>
                                setHasAgreed(event.target.checked)
                            }
                        />
                        <CheckBox.Label>
                            <FormattedMessage
                                id="_.funding_requirements_v2.credit_agreement.agree"
                                defaultMessage="I have read the loan agreement and agree to the terms and conditions."
                            />
                        </CheckBox.Label>
                    </CheckBox.Root>
                    {hasTriedToSign && !hasAgreed && (
                        <ErrorMessage>
                            <FormattedMessage
                                id="_.funding_requirements_v2.credit_agreement.agree_error"
                                defaultMessage="Please confirm"
                            />
                        </ErrorMessage>
                    )}
                </Stack>

                <Flex align="center" gap="comfortable">
                    {/* ORION-113-GAP off-system: hand-styled outline pill link (download) instead of an Orion Button variant; its inner layout and size stay as classes */}
                    <a
                        href={downloadUrl}
                        download
                        className="flex min-h-[52px] items-center gap-m rounded-full border border-primary-10 px-l text-l font-bold text-primary-10 hover:bg-structure-95"
                    >
                        <FormattedMessage
                            id="_.funding_requirements_v2.credit_agreement.download"
                            defaultMessage="Download"
                        />
                        <IconDownload className="text-xl" />
                    </a>
                    <Flex.Item grow>
                        {/* ORION-113-GAP component: Flex.Item fills, but a control inside it needs a width class to fill the item */}
                        <Button
                            variant="primary-rounded"
                            size="large"
                            loading={isSigning}
                            onClick={handleSign}
                            className="w-full"
                        >
                            <FormattedMessage
                                id="_.funding_requirements_v2.credit_agreement.sign"
                                defaultMessage="Sign"
                            />
                        </Button>
                    </Flex.Item>
                </Flex>
            </Stack>
        </Stack>
    );
};
